/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import { HTTPHdrAction } from 'object-types';

import {
    HTTPHdrActionType,
    HTTPPolicyVar,
    IHTTPHdrAction,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { StringService } from 'string-service';

import { HTTPHdrDataConfigItem } from
    // eslint-disable-next-line max-len
    'ajs/modules/http-policy-set/factories/http-hdr-data.config-item.factory';

import * as globalL10n from 'global-l10n';
import * as l10n from '../http-policy-set.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

type THTTPHdrAction = Omit<IHTTPHdrAction, 'hdr'>;
interface IHTTPHdrActionConfig extends THTTPHdrAction {
    hdr?: HTTPHdrDataConfigItem;
}

/**
 * Using values from HTTPPolicyVar enum to
 * implement checks on component template.
 */
export const HTTPPolicyVarHash = {
    HTTP_POLICY_VAR_HTTP_HDR:
    HTTPPolicyVar.HTTP_POLICY_VAR_HTTP_HDR,
    CUSTOM_VALUE: 'CUSTOM_VALUE',
};

/**
 * @description HTTPHdrAction ConfigItem.
 *
 * @author Abhinesh Gour
 */
export class HTTPHdrActionConfigItem extends MessageItem<IHTTPHdrActionConfig> {
    public static ajsDependencies = [
        'stringService',
        'l10nService',
    ];

    /**
     * L10nService instance.
     */
    private readonly l10nService: L10nService;

    /**
     * StringService instance to work with string values.
     */
    private readonly stringService: StringService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPHdrAction,
            ...args,
        };

        super(extendedArgs);
        this.stringService = this.getAjsDependency_('stringService');
        this.l10nService = this.getAjsDependency_('l10nService');
    }

    /**
     * Extracts value from the config and prepares a string
     * to show action details in the grid expander.
     */
    public actionReadonlyDetails(): string {
        const OBFUSCATE = '••••••••';
        let actionText = '';

        const { action, hdr: { config: hdrConfig } } = this.config;
        const { value: { config: valueConfig } } = hdrConfig;
        const { val = '', is_sensitive: isSensitive = false } = valueConfig ?? {};

        const hdrVar = valueConfig?.var ?
            this.stringService.enumeration(valueConfig.var, 'HTTP_') :
            this.l10nService.getMessage(globalL10nKeys.customValueLabel);

        const hdrVal = isSensitive && val ? OBFUSCATE : val;

        actionText += this.stringService.enumeration(action, 'HTTP_');

        switch (action) {
            case HTTPHdrActionType.HTTP_REPLACE_HDR:
                actionText += ` "${hdrConfig.name}"
                ${this.l10nService.getMessage(l10nKeys.withLabel)} ${hdrVar} - ${hdrVal}`;
                break;
            case HTTPHdrActionType.HTTP_ADD_HDR:
                actionText += ` "${hdrConfig.name}" : ${hdrVar} - ${hdrVal}`;
                break;
            case HTTPHdrActionType.HTTP_REMOVE_HDR:
                actionText += ` "${hdrConfig.name}"`;
                break;
        }

        return actionText;
    }

    /**
     * @override
     */
    protected modifyConfigDataAfterLoad(): void {
        super.modifyConfigDataAfterLoad();

        const { hdr: header } = this.config;
        const { config: { value } } = header;

        if (value?.config) {
            const { config: { var: httpVar } } = value;

            if (!httpVar) {
                this.config.hdr.config.value.config.var = HTTPPolicyVarHash.CUSTOM_VALUE;
            }
        }

        if (!header) {
            this.safeSetNewChildByField('hdr');
        }
    }

    /**
     * @override
     */
    protected modifyConfigDataBeforeSave(): void {
        super.modifyConfigDataBeforeSave();

        const { hdr: { config: { value } } } = this.config;

        if (value?.config?.var) {
            const { config: { var: httpVar } } = value;

            if (httpVar === HTTPPolicyVarHash.CUSTOM_VALUE) {
                delete this.config.hdr.config.value.config.var;
            }
        }
    }
}
